import { useAuthStore } from '../stores/auth'
import { mutePush, syncPush } from './device'

const DISMISS_KEY = 'dc-push-ask'
const MUTE_KEY = 'dc-push-mute'

export function pushSupported(): boolean {
  return (
    typeof Notification !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  )
}

export function pushAskDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

export function dismissPushAsk(): void {
  try {
    localStorage.setItem(DISMISS_KEY, '1')
  } catch {
    /* private mode keeps the choice for this visit */
  }
}

export function pushMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1'
  } catch {
    return false
  }
}

function writePushMuted(on: boolean): void {
  try {
    if (on) localStorage.setItem(MUTE_KEY, '1')
    else localStorage.removeItem(MUTE_KEY)
  } catch {
    /* private mode keeps the choice for this visit */
  }
}

/** True when this browser should show or deliver tournament notices. */
export function tournamentNoticesAllowed(): boolean {
  return pushSupported() && Notification.permission === 'granted' && !pushMuted()
}

/** Saves the push subscription when notices are already allowed. Never prompts. */
export function armTournamentPush(): void {
  if (!tournamentNoticesAllowed()) return
  const token = useAuthStore().token
  if (!token) return
  void syncPush(token)
}

/**
 * Browser permission prompt. Call only from a click: Android ignores it otherwise.
 * When the player allows notices, this browser is saved for tournament push.
 * Does not override an explicit mute on this device.
 */
export function askTournamentPush(): Promise<NotificationPermission | 'unsupported'> {
  if (!pushSupported()) return Promise.resolve('unsupported')
  if (Notification.permission === 'granted') {
    armTournamentPush()
    return Promise.resolve('granted')
  }
  if (Notification.permission !== 'default') return Promise.resolve(Notification.permission)
  return Notification.requestPermission()
    .then((perm) => {
      if (perm === 'granted') armTournamentPush()
      return perm
    })
    .catch(() => Notification.permission)
}

/** Turns tournament notices back on for this browser. Call from a click. */
export function enableTournamentPush(): Promise<NotificationPermission | 'unsupported'> {
  writePushMuted(false)
  return askTournamentPush()
}

/** Drops tournament push on this browser. Call from a click. */
export function muteTournamentPush(): Promise<void> {
  writePushMuted(true)
  return mutePush(useAuthStore().token ?? '')
}
